// Split-key field encryption — client-side groundwork per docs/SPLIT-KEY-SCHEME.md
// and API-CONTRACT §5.10 (Split-Key Material & Events).
//
// MODEL (recommended Option 2 — HKDF combine):
//   DEK = HKDF(SharedSecret || SubscriberSecret, info = {profile, version})
//   - SharedSecret            : fetched from GET /profiles/{id}/key-material (same for all subs)
//   - SubscriberSecret        : fetched wrapped (encrypted to THIS subscriber's public key),
//                               unwrapped locally with the subscriber private key (never sent up)
//   - Field ciphertext        : AEAD (AES-256-GCM), AAD binds {identity, profile, field, version}
//
// This module implements the CLIENT half that is fully determinable from the docs:
//   1. subscriber keypair generation (the public key we register at subscribe time),
//   2. HKDF combine → DEK,
//   3. AES-GCM field decrypt with AAD.
//
// It STUBS, with explicit TODOs, the parts whose exact server wire-format is not yet
// pinned in the OpenAPI spec (KeyMaterial only names `shared_secret`,
// `subscriber_secret_wrapped`, `kdf` as opaque strings — no algorithm/encoding fixed,
// no ciphertext envelope schema). We DO NOT fake security: where the format is unknown
// we throw a clear "not yet specced" error rather than silently returning plaintext.

import { b64urlDecode, b64urlEncode, fromUtf8, utf8 } from './base64.js';

const DB_NAME = 'virtualid-portal';
const STORE = 'keys';
const SUB_KEY_ID = 'subscriber-ecdh';

// --- Subscriber keypair (ECDH P-256). Private key non-extractable, persisted as a
//     CryptoKey handle in IndexedDB (structured-clone preserves non-extractable keys). ---

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
    tx.onsuccess = () => resolve(tx.result as T);
    tx.onerror = () => reject(tx.error);
  });
}
async function idbPut(key: string, val: unknown): Promise<void> {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite').objectStore(STORE).put(val, key);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export interface SubscriberKeyInfo {
  publicJwk: JsonWebKey; // registered with the server at subscribe time
}

/** Ensure a persistent subscriber ECDH keypair exists; return the public JWK to register. */
export async function ensureSubscriberKeypair(): Promise<SubscriberKeyInfo> {
  const existing = await idbGet<CryptoKeyPair>(SUB_KEY_ID);
  if (existing) {
    const publicJwk = await crypto.subtle.exportKey('jwk', existing.publicKey);
    return { publicJwk };
  }
  const kp = (await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false, // non-extractable private key
    ['deriveBits'],
  )) as CryptoKeyPair;
  await idbPut(SUB_KEY_ID, kp);
  const publicJwk = await crypto.subtle.exportKey('jwk', kp.publicKey);
  return { publicJwk };
}

export async function getSubscriberPrivateKey(): Promise<CryptoKey | undefined> {
  const kp = await idbGet<CryptoKeyPair>(SUB_KEY_ID);
  return kp?.privateKey;
}

// --- HKDF combine: DEK = HKDF(sharedSecret || subscriberSecret, info={profile,version}) ---

export async function combineDEK(
  sharedSecret: Uint8Array,
  subscriberSecret: Uint8Array,
  profileId: string,
  version: number,
): Promise<CryptoKey> {
  const ikm = new Uint8Array(sharedSecret.length + subscriberSecret.length);
  ikm.set(sharedSecret, 0);
  ikm.set(subscriberSecret, sharedSecret.length);

  const baseKey = await crypto.subtle.importKey('raw', ikm as unknown as ArrayBuffer, 'HKDF', false, ['deriveKey']);
  const info = utf8(JSON.stringify({ profile: profileId, version }));
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0) as unknown as ArrayBuffer, info: info as unknown as ArrayBuffer },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt', 'encrypt'],
  );
}

/**
 * Unwrap the per-subscriber secret. The KeyMaterial schema only gives us an opaque
 * `subscriber_secret_wrapped` + `kdf` string; the wrapping algorithm (plain ECDH-ES,
 * HPKE/RFC 9180, or RSA-OAEP) is NOT pinned in the spec yet.
 *
 * TODO(server-contract): once the KeyMaterial envelope is finalized (recommended HPKE
 * per SPLIT-KEY-SCHEME §Improvements 5), implement the matching unwrap here. Until then
 * we support the simplest documented path (ECDH-ES + HKDF unwrap of a raw secret) and
 * fail loudly on anything else so we never hand back an unauthenticated key.
 */
export async function unwrapSubscriberSecret(km: {
  subscriber_secret_wrapped: string;
  kdf?: string;
  ephemeral_public_jwk?: JsonWebKey; // present in ECDH-ES envelopes
}): Promise<Uint8Array> {
  const priv = await getSubscriberPrivateKey();
  if (!priv) throw new Error('No subscriber private key on this device — subscribe first.');

  if (km.ephemeral_public_jwk && (!km.kdf || km.kdf.startsWith('ecdh'))) {
    const ephPub = await crypto.subtle.importKey(
      'jwk', km.ephemeral_public_jwk, { name: 'ECDH', namedCurve: 'P-256' }, false, [],
    );
    const shared = await crypto.subtle.deriveBits({ name: 'ECDH', public: ephPub }, priv, 256);
    // The derived ECDH secret is the KEK; the wrapped secret is AES-GCM(KEK, nonce||ct).
    const wrapped = b64urlDecode(km.subscriber_secret_wrapped);
    const kek = await crypto.subtle.importKey('raw', shared, 'AES-GCM', false, ['decrypt']);
    const iv = wrapped.slice(0, 12);
    const ct = wrapped.slice(12);
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer }, kek, ct as unknown as ArrayBuffer,
    );
    return new Uint8Array(pt);
  }

  // TODO(server-contract): HPKE (RFC 9180) unwrap path — no WebCrypto native support yet,
  // would need a small HPKE polyfill. Not implemented rather than faked.
  throw new Error(
    `Subscriber-secret unwrap format not yet supported (kdf="${km.kdf ?? 'unknown'}"). ` +
    `Server KeyMaterial envelope must be finalized (see SPLIT-KEY-SCHEME.md).`,
  );
}

// --- Field decrypt (AES-256-GCM with AAD binding). ---

export interface EncryptedField {
  iv: string;         // base64url, 12 bytes
  ciphertext: string; // base64url, includes GCM tag
  aad?: string;       // base64url of the canonical AAD the server bound
}

export async function decryptField(dek: CryptoKey, field: EncryptedField): Promise<string> {
  const iv = b64urlDecode(field.iv);
  const ct = b64urlDecode(field.ciphertext);
  const params: AesGcmParams = { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer };
  if (field.aad) params.additionalData = b64urlDecode(field.aad) as unknown as ArrayBuffer;
  const pt = await crypto.subtle.decrypt(params, dek, ct as unknown as ArrayBuffer);
  return fromUtf8(pt);
}

/**
 * Encrypt a local private note (My Contacts notes are private to the owner and stored
 * encrypted per MVP-SCOPE §2). We derive a note-key from the subscriber keypair so notes
 * are decryptable only on the user's devices. Groundwork — the exact server storage
 * envelope for annotations isn't pinned, so callers currently keep notes client-side.
 */
export async function noteKey(): Promise<CryptoKey> {
  const priv = await getSubscriberPrivateKey();
  if (!priv) {
    // Fallback: ephemeral per-session key (notes won't persist across reload). TODO: bind to account key.
    return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  }
  // Self-ECDH to derive a stable symmetric key from the device private key.
  const pubJwk = (await ensureSubscriberKeypair()).publicJwk;
  const pub = await crypto.subtle.importKey('jwk', pubJwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: pub }, priv, 256);
  return crypto.subtle.importKey('raw', bits, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

export async function encryptNote(plain: string): Promise<EncryptedField> {
  const key = await noteKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer }, key, utf8(plain) as unknown as ArrayBuffer,
  );
  return { iv: b64urlEncode(iv), ciphertext: b64urlEncode(ct) };
}

export async function decryptNote(field: EncryptedField): Promise<string> {
  const key = await noteKey();
  return decryptField(key, field);
}
